// src/pages/AdminBrandsPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminBrandsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminBrandsPage() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    image: null,
    is_active: true,
    sort_order: "",
  });

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const resolveImageUrl = (item) => {
    if (!item) return null;
    if (typeof item.image_url === "string" && item.image_url) return item.image_url;
    if (typeof item.image === "string" && item.image) return item.image;
    if (item.image && typeof item.image === "object") {
      if (item.image.url) return item.image.url;
      if (item.image.path)
        return `${API_BASE.replace("/api", "")}/storage/${item.image.path}`;
    }
    return null;
  };

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");
    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }
    fetchBrands(token);
  }, [navigate]);

  const fetchBrands = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/brands`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.brands)
          ? data.brands
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        // Sort brands by sort_order, then by id
        const sortedBrands = list.sort((a, b) => {
          const aOrder = parseInt(a.sort_order) || 0;
          const bOrder = parseInt(b.sort_order) || 0;
          if (aOrder !== bOrder) {
            return aOrder - bOrder;
          }
          return a.id - b.id;
        });
        setBrands(sortedBrands);
      } else {
        toast.error("Failed to fetch brands");
        setBrands([]);
      }
    } catch (error) {
      toast.error("Error fetching brands");
      console.error("Error:", error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked :
        type === "file" ? files[0] :
        value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // Guard: required name (trim)
    const nameTrimmed = (formData.name || "").trim();
    if (!nameTrimmed) {
      toast.error("Brand name is required");
      return;
    }

    const fd = new FormData();
    fd.append("name", nameTrimmed);
    fd.append("is_active", formData.is_active ? "1" : "0");
    fd.append("sort_order", formData.sort_order || "0");
    if (formData.image) fd.append("image", formData.image);

    const isEdit = Boolean(editingBrand);
    const url = isEdit
      ? `${API_BASE}/brands/${editingBrand.id}`
      : `${API_BASE}/brands`;

    if (isEdit) fd.append("_method", "PUT"); // spoof PUT for multipart

    try {
      const response = await fetch(url, {
        method: "POST", // always POST with FormData
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: fd,
      });

      if (response.ok) {
        toast.success(isEdit ? "Brand updated successfully" : "Brand created successfully");
        setShowModal(false);
        setEditingBrand(null);
        resetForm();
        fetchBrands(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Show first validation error if present
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          toast.error(firstMsg || errorData.message || "Failed to save brand");
        } else {
          toast.error(errorData.message || "Failed to save brand");
        }
        console.error("Save error detail:", errorData);
      }
    } catch (error) {
      toast.error("Error saving brand");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand?.name || "",
      image: null,
      is_active: Boolean(brand?.is_active),
      sort_order: brand?.sort_order || "0",
    });
    setShowModal(true);
  };

  const handleDelete = async (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    setDeletingBrand(brand);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingBrand) return;
    
    setIsDeleting(true);
    const token = getToken();
    try {
      const response = await fetch(`${API_BASE}/brands/${deletingBrand.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        toast.success("Brand deleted successfully");
        setShowDeleteModal(false);
        setDeletingBrand(null);
        fetchBrands(token);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete brand");
      }
    } catch (error) {
      toast.error("Error deleting brand");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingBrand(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      image: null,
      is_active: true,
      sort_order: "",
    });
  };

  const openModal = () => {
    setEditingBrand(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBrand(null);
    resetForm();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-brands">
          <div className="loading">Loading brands...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-brands-page">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Brands Management</h1>
            <p>Manage product brands and manufacturers</p>
          </div>
          <div className="admin-actions">
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Brand
            </button>
          </div>
        </div>

        {/* Brands Grid */}
        <div className="admin-brands-grid">
          {brands.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" />
                </svg>
              </div>
              <h3>No brands found</h3>
              <p>Create your first brand to get started</p>
            </div>
          ) : (
            brands.map((brand) => {
              const imgUrl = resolveImageUrl(brand);
              return (
                <div key={brand.id} className="admin-brand-card">
                  <div className="brand-image">
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={brand.name}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className="image-placeholder"
                      style={{ display: imgUrl ? "none" : "flex" }}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" />
                      </svg>
                    </div>
                  </div>

                  <div className="admin-brand-info">
                    <div className="admin-brand-details">
                      <h3 className="admin-brand-name">{brand.name}</h3>
                      <div className="admin-brand-meta">
                        <div className="admin-brand-order">
                          <span className="admin-label">Order:</span>
                          <span className="admin-value">#{parseInt(brand.sort_order) || 0}</span>
                        </div>
                        <div className="admin-brand-status">
                          <span className="admin-label">Status:</span>
                          <span className={`admin-status ${brand.is_active ? "active" : "inactive"}`}>
                            {brand.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div className="admin-brand-date">
                          <span className="admin-label">Created:</span>
                          <span className="admin-value">
                            {brand.created_at ? new Date(brand.created_at).toLocaleDateString() : "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-brand-actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(brand)}
                        title="Edit Brand"
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" />
                          <path d="M18.5 2.5a2 2 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(brand.id)}
                        title="Delete Brand"
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" fill="none" stroke="currentColor" strokeWidth="2" />
                          <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" />
                          <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingBrand ? "Edit Brand" : "Add New Brand"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="name">Brand Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter brand name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sort_order">Sort Order</label>
                  <input
                    type="number"
                    id="sort_order"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Enter sort order (0 = first)"
                  />
                  <small className="form-help">
                    Lower numbers appear first in the brand list
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="image">Brand Logo</label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                    required={!editingBrand}
                  />
                  {editingBrand && resolveImageUrl(editingBrand) && (
                    <div className="current-image">
                      <p>Current logo:</p>
                      <img src={resolveImageUrl(editingBrand)} alt="Current" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>Active Brand</span>
                  </label>
                  <small className="form-help">
                    Only active brands will be available for product selection
                  </small>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingBrand ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingBrand ? "Update Brand" : "Create Brand"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Brand"
          message="Are you sure you want to delete this brand?"
          itemName={deletingBrand ? deletingBrand.name : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
